import { TaskConstants } from "../../../../../constants/workflow/TaskConstants";
import {
  SearchWorkflowTaskObjectGroupsFilter,
  SearchWorkflowTaskObjectGroupsRequest,
  SearchWorkflowTaskObjectGroupsResponse,
  SearchWorkflowTaskObjectGroupsWhere,
  SearchWorkflowTasksRequest,
  SearchWorkflowTasksResponse,
} from "../../../../../service/query";
import { convertDateToStringDDMMYYYY, convertDateToUtcStartOfDay, isPastDateByDay } from "../../../../../utils";
import { KanbanColumnId, KanbanTask, TaskCardObjectType } from "../../../../../widget";
import {
  COMPLETED_COLUMN_ID,
  KANBAN_COLUMN_ORDER,
  UNASSIGNED_COLUMN_ID,
} from "../../../../../widget/general/TasksDashboard/constants";
import { TasksDashboardGroup, TasksDashboardQueryFilters } from "../models";

type SearchWorkflowTaskNode = SearchWorkflowTasksResponse["results"][number];
type SearchWorkflowTasksFilter = NonNullable<SearchWorkflowTasksRequest["filter"]>;
type SearchWorkflowTaskCriteria = NonNullable<SearchWorkflowTasksFilter["results"]>;

/** The status string each Kanban lane queries on (§2c of KAN-9561). */
export const STATUS_BY_COLUMN_ID: Record<KanbanColumnId, string> = {
  unassigned: TaskConstants.TASK_STATUS_UNASSIGNED,
  "not-started": TaskConstants.TASK_STATUS_TO_DO,
  "in-progress": TaskConstants.TASK_STATUS_IN_PROGRESS,
  "in-review": TaskConstants.TASK_STATUS_IN_REVIEW,
  blocked: TaskConstants.TASK_STATUS_BLOCKED,
  completed: TaskConstants.TASK_STATUS_COMPLETE,
};

const COLUMN_ID_BY_STATUS: Record<string, KanbanColumnId> = Object.entries(STATUS_BY_COLUMN_ID).reduce(
  (map, [columnId, status]) => ({ ...map, [status]: columnId as KanbanColumnId }),
  {}
);

/** Lane id → the `status.eq` string its query filters on. */
export const mapColumnIdToStatus = (columnId: KanbanColumnId): string => STATUS_BY_COLUMN_ID[columnId];

/**
 * BE status string → lane id. An unrecognised status falls back to "not-started" so a status added
 * server-side still renders a card somewhere rather than dropping it from the board.
 */
export const mapStatusToColumnId = (status: string): KanbanColumnId => COLUMN_ID_BY_STATUS[status] ?? "not-started";

/** "Ada Lovelace" → "AL". Used for the card avatar when the API gives us only a display name. */
const getInitials = (name: string): string =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

/** The task card only badges the three parent types; anything else renders without a type badge. */
const toCardObjectType = (objectType: string | null): TaskCardObjectType | undefined => {
  const normalised = objectType?.toLowerCase();
  if (normalised === "project" || normalised === "deal" || normalised === "landholding") return normalised;
  return undefined;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Whole days late. Both sides are UTC day starts, so the division is exact and DST-proof. */
const getDaysOverdue = (deadlineDate: Date): number =>
  (convertDateToUtcStartOfDay(new Date()).getTime() - convertDateToUtcStartOfDay(deadlineDate).getTime()) / MS_PER_DAY;

/**
 * Search result → the widget's `KanbanTask`.
 *
 * The linked object is the *uniform* parent (`parentObject*`), which spans Project / Deal /
 * Landholding and is set for general tasks too — `workflowObject*` only covers tasks that hang off
 * a workflow. Null-safe on `step` (a general task has none): that nullability is what tells the two
 * task types apart, so it drives `isGeneral` / `hasWorkflow` rather than any separate flag.
 */
export const mapNodeToKanbanTask = (node: SearchWorkflowTaskNode): KanbanTask => {
  const columnId = mapStatusToColumnId(node.status);
  /*
    Overdue is suppressed on the Complete lane — a task finished after its deadline is not overdue any
    more (REQ-009). The deadline is date-only and arrives parsed as local midnight, so both it and
    "now" are taken to their UTC day start before comparing — the same normalisation the rest of the
    app uses for date-only values, and what keeps "due today" out of overdue in every timezone.
  */
  const isOverdue =
    node.deadlineDate != null &&
    columnId !== COMPLETED_COLUMN_ID &&
    isPastDateByDay(convertDateToUtcStartOfDay(node.deadlineDate), convertDateToUtcStartOfDay(new Date()));

  return {
    id: node.uuid,
    status: columnId,
    title: node.name,
    linkedObjectName: node.parentObjectName ?? "",
    linkedObjectType: toCardObjectType(node.parentObjectType),
    linkedObjectUuid: node.parentObjectUuid ?? undefined,
    assigneeName: node.assignee?.name ?? undefined,
    assigneeInitials: node.assignee?.name ? getInitials(node.assignee.name) : undefined,
    assigneeUuid: node.assignee?.uuid ?? undefined,
    dueDate: node.deadlineDate ? convertDateToStringDDMMYYYY(node.deadlineDate) : "",
    isOverdue,
    daysOverdue: isOverdue && node.deadlineDate ? getDaysOverdue(node.deadlineDate) : undefined,
    isMandatory: node.isMandatory,
    isMilestone: node.isMilestone,
    isGeneral: node.step == null,
    hasWorkflow: node.step != null,
    tags: node.tags?.map((tag) => tag.tagName) ?? [],
    stage: node.step?.name,
    phase: node.step?.phase ?? undefined,
  };
};

/** Whether a lane is hidden by the header's toggles, and so needs no query at all. */
export const isColumnHidden = (columnId: KanbanColumnId, filters: TasksDashboardQueryFilters): boolean =>
  (Boolean(filters.hideCompleted) && columnId === COMPLETED_COLUMN_ID) ||
  (Boolean(filters.hideUnassigned) && columnId === UNASSIGNED_COLUMN_ID);

/**
 * The statuses the toggles leave visible, or `undefined` when neither toggle is on (i.e. no status
 * restriction to send). Used for the group query's `statuses` and for the leaf queries that load a
 * whole object rather than one status.
 */
export const getVisibleStatuses = (filters: TasksDashboardQueryFilters): string[] | undefined => {
  if (!filters.hideCompleted && !filters.hideUnassigned) return undefined;

  return KANBAN_COLUMN_ORDER.filter((columnId) => !isColumnHidden(columnId, filters)).map(mapColumnIdToStatus);
};

/**
 * Builds the shared task-level `where.results` — once — from the page's filter selections
 * (BR-02828). It is spread into every lane query and every group-leaf query with only `status`
 * (and, for a leaf, `parentObjectUuid`) added.
 */
export const buildTaskFilter = (filters: TasksDashboardQueryFilters): SearchWorkflowTaskCriteria => {
  const results: SearchWorkflowTaskCriteria = {};

  /*
    Every filter is multi-select, so each one is an `in` over the whole selection — the values within a
    filter match as OR while the filters stay additive (AND). `in` is used even for a single value, so
    there is one shape of clause rather than two.
  */
  if (filters.assigneeUuids?.length) {
    results.assignee = { uuid: { operator: "in", value: filters.assigneeUuids } };
  }

  /*
    Task type is the nullability of the task's workflow: a general task has none. Filtered
    server-side (never over the loaded cards) so the lane totals stay correct. A task is exactly one
    of the two types, so selecting both is no restriction — there is no clause to send.

    Asked of `workflowUuid` rather than of `step.uuid`, even though the row model derives `isGeneral`
    from `step`: `step.uuid` is non-nullable server-side (`UUID!`), so `eq: null` against it is
    rejected outright — "The provided value for filter `eq` of type UuidOperationFilterInput is
    invalid. Null values are not supported." `workflowUuid` is nullable, and a task has a workflow
    exactly when it has a step.
  */
  if (filters.taskTypes?.length === 1) {
    results.workflowUuid = { operator: filters.taskTypes[0] === "general" ? "eq" : "neq", value: null };
  }

  // The uniform parent, so one filter covers a Project, a Deal and a Landholding.
  if (filters.parentObjectUuids?.length) {
    results.parentObjectUuid = { operator: "in", value: filters.parentObjectUuids };
  }

  if (filters.tagUuids?.length) {
    // `some` over an `in`: a task matches when any of its tags is any of the selected ones.
    results.tags = { some: { tagUuid: { operator: "in", value: filters.tagUuids } } };
  }

  return results;
};

/** The shared filter plus this lane's status — the only per-lane difference. */
export const buildLaneRequest = (
  columnId: KanbanColumnId,
  sharedFilter: SearchWorkflowTaskCriteria,
  pageSize: number,
  afterCursor: string | null
): SearchWorkflowTasksRequest => ({
  paging: { limit: pageSize, beforeCursor: null, afterCursor },
  sort: [{ key: "results.deadlineDate", direction: "asc" }],
  filter: {
    results: {
      ...sharedFilter,
      status: { operator: "eq", value: mapColumnIdToStatus(columnId) },
    },
  },
  // No workflowObjectUuid restriction: the dashboard feed is organisation-wide (REQ-002), which is
  // the one difference from the per-project board in `useTasks`.
});

/**
 * A group's leaf rows: the shared filter plus that group's parent object, and one status when the
 * caller slices by status. The Gantt loads a whole object (no `status`); the List loads one status
 * section at a time.
 */
export const buildGroupTasksRequest = (
  objectUuid: string,
  status: string | undefined,
  sharedFilter: SearchWorkflowTaskCriteria,
  pageSize: number,
  afterCursor: string | null
): SearchWorkflowTasksRequest => ({
  paging: { limit: pageSize, beforeCursor: null, afterCursor },
  sort: [{ key: "results.deadlineDate", direction: "asc" }],
  filter: {
    results: {
      ...sharedFilter,
      parentObjectUuid: { operator: "eq", value: objectUuid },
      ...(status === undefined ? {} : { status: { operator: "eq" as const, value: status } }),
    },
  },
});

/**
 * One task by uuid, with the full node the task panel edits against. Fetched on open rather than
 * cached from the board: the panel's mutations carry `rowVersion`, so it must be the current row and
 * not whatever the last page load happened to see.
 */
export const buildTaskByUuidRequest = (taskUuid: string): SearchWorkflowTasksRequest => ({
  paging: { limit: 1, beforeCursor: null, afterCursor: null },
  sort: [],
  filter: { results: { uuid: { operator: "eq", value: taskUuid } } },
});

/**
 * The ungrouped leaf request: the shared filter and nothing else — no `parentObjectUuid` and no
 * `status` — ordered by deadline. Used wherever the whole filtered task set is wanted flat rather than
 * per group: the CSV export (KAN-9564 §6, option B, which reapplies the grouping client-side from
 * `parentObject*`) and the Gantt's Agenda view, which is one date-sorted list across every object.
 * The hide toggles still apply, through the connection's status filter.
 */
export const buildFlatTasksRequest = (
  sharedFilter: SearchWorkflowTaskCriteria,
  pageSize: number,
  afterCursor: string | null
): SearchWorkflowTasksRequest => ({
  paging: { limit: pageSize, beforeCursor: null, afterCursor },
  sort: [{ key: "results.deadlineDate", direction: "asc" }],
  filter: { results: { ...sharedFilter } },
});

/**
 * The same filter selections translated to the group query's `request` input. The group counts and
 * the leaf rows are therefore filtered identically — a header reading "12" always has 12 rows under
 * it (BR-02828).
 */
export const buildGroupsFilter = (filters: TasksDashboardQueryFilters): SearchWorkflowTaskObjectGroupsFilter => ({
  assigneeUuids: filters.assigneeUuids?.length ? filters.assigneeUuids : null,
  tagUuids: filters.tagUuids?.length ? filters.tagUuids : null,
  /*
    The task type is the nullability of the workflow step, which the group input exposes as a single
    flag — so it can only carry one of the two types. Both selected is no restriction anyway (a task is
    exactly one of them), which is the same `null` the flag uses for "either".
  */
  isGeneralTask: filters.taskTypes?.length === 1 ? filters.taskTypes[0] === "general" : null,
  // Hidden statuses are excluded from the counts, so a header never counts rows the toggle hides.
  statuses: getVisibleStatuses(filters) ?? null,
});

/**
 * The object filter narrows the group rows rather than the tasks they count, so it is the group
 * connection's `where` — the `request` input has no object field.
 */
export const buildGroupsWhere = (
  filters: TasksDashboardQueryFilters
): SearchWorkflowTaskObjectGroupsWhere | undefined =>
  filters.parentObjectUuids?.length ? { objectUuid: { operator: "in", value: filters.parentObjectUuids } } : undefined;

export const buildGroupsRequest = (
  filters: TasksDashboardQueryFilters,
  pageSize: number,
  afterCursor: string | null
): SearchWorkflowTaskObjectGroupsRequest => ({
  first: pageSize,
  after: afterCursor,
  request: buildGroupsFilter(filters),
  where: buildGroupsWhere(filters),
});

/** Group header → the row model the List and Gantt views read, with counts keyed by lane. */
export const mapNodeToGroup = (
  node: SearchWorkflowTaskObjectGroupsResponse["results"][number]
): TasksDashboardGroup => ({
  objectType: node.objectType,
  objectUuid: node.objectUuid,
  objectName: node.objectName,
  taskCount: node.taskCount,
  milestoneTaskCount: node.milestoneTaskCount,
  overdueTaskCount: node.overdueTaskCount,
  statusCounts: node.statusCounts,
  countsByColumnId: node.statusCounts.reduce<Partial<Record<KanbanColumnId, number>>>((counts, { status, count }) => {
    const columnId = mapStatusToColumnId(status);
    return { ...counts, [columnId]: (counts[columnId] ?? 0) + count };
  }, {}),
});

/**
 * Cache key for one group's leaf rows, loaded lazily on expand. `status` is omitted when the whole
 * object is loaded in one go (the Gantt) and set when it is sliced per status (the List), so the two
 * never collide in the cache.
 */
export const getGroupTasksKey = (objectUuid: string, status?: string): string => `${objectUuid}|${status ?? "*"}`;
